// lib/api.ts
import axios, { AxiosRequestConfig, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { getCookie } from 'cookies-next';
import { clearAuthCookies } from '@/lib/authCookies';
import { toast } from 'react-toastify';
import { showGlobalErrorModal } from '@/utils/errorModal';

// Utility function to extract path from URL
const extractPath = (url: string | undefined): string => {
  if (!url) return 'unknown';
  // Remove query parameters and hash, keep only the path
  const path = url.split('?')[0].split('#')[0];
  return path || 'unknown';
};

// Remove password-related fields before sending request data to the error API
const redactSensitiveFields = (value: unknown): unknown => {
  if (value == null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(redactSensitiveFields);

  const redacted: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (/password/i.test(key)) {
      redacted[key] = '[REDACTED]';
    } else if (val != null && typeof val === 'object') {
      redacted[key] = redactSensitiveFields(val);
    } else {
      redacted[key] = val;
    }
  }
  return redacted;
};

const serializeRequestData = (data: unknown): string => {
  let parsed = data;
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data);
    } catch {
      // Keep raw string if it is not JSON (e.g. form-urlencoded)
      return /password=/i.test(data)
        ? data.replace(/([Pp]assword[^=&]*)=[^&]*/g, '$1=[REDACTED]')
        : data;
    }
  }

  const safeData = redactSensitiveFields(parsed);
  const payloadStr = typeof safeData === 'string' ? safeData : JSON.stringify(safeData);
  return (payloadStr.length > 500
    ? payloadStr.substring(0, 500) + '... [truncated]'
    : payloadStr
  ).replace(/'/g, '"');
};

// Utility function to get user IP address
async function getUserIpAddress(): Promise<string> {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 2000);
    
    const ipResponse = await fetch('https://api.ipify.org?format=json', {
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    
    if (ipResponse.ok) {
      const ipData = await ipResponse.json();
      return ipData.ip || 'Unknown';
    }
  } catch (ipError: any) {
    if (ipError.name !== 'AbortError') {
      console.warn('Failed to fetch IP address:', ipError);
    }
  }
  return 'Unknown';
}

// Create Axios instance
console.log('API URL being used:', process.env.NEXT_PUBLIC_API_URL);
const authenticationApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth headers
authenticationApi.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Check if there are any active push notification toasts
    const pushNotificationToasts = (typeof window !== 'undefined' && (window as any).__pushNotificationToastIds) 
      ? (window as any).__pushNotificationToastIds as Set<string>
      : new Set<string>();
    
    // If there are active push notification toasts, skip dismissing toasts entirely
    // to prevent push notification toasts from being dismissed
    if (pushNotificationToasts.size > 0) {
      console.log('[Auth API Interceptor] Skipping toast dismissal - push notification toasts are active');
    } else {
      // Only dismiss toasts if there are no push notification toasts
      toast.dismiss();
    }

    const token = getCookie("token"); // Adjust based on your auth setup
    if (token && config.headers) {
      config.headers.Token = `${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    // Handle request setup errors (e.g., invalid config)
    return Promise.reject({
      message: 'Request configuration failed',
      details: error.message,
    });
  }
);

// Response interceptor for error handling
authenticationApi.interceptors.response.use(
  (response) => {
    // Return successful responses directly
    return response;
  },
  (error: AxiosError) => {
    // Extract response data and status
    const { response, request, config } = error;
    let errorMessage = 'An unexpected error occurred';
    const statusCode = response?.status;

    const token = getCookie("token");

    let errorApiCall = "";

    if(token) {
      errorApiCall = "/v1/care_ascendium_error/new";
    } else {
      errorApiCall = "/v1/care_ascendium_error_outside/new";
    }

    const requestUrl = config?.url || 'unknown';
    const baseUrl = config?.baseURL || 'unknown';
    const requestPath = extractPath(config?.url); // Capture request path

    // Create a custom error object
    const customError = {
      message: errorMessage,
      status: statusCode,
      details: response?.data || error.message,
    };

    const details = {
      message: "",
      error_message: ""
    }

    if (response) {
      // Server responded with a status code
      if (statusCode == 401) {
          // Optional: Trigger logout or token refresh
          clearAuthCookies();

          window.location.href = '/login';
      }

      if (statusCode == 400 || statusCode == 404 || statusCode == 500 || statusCode == 502 || statusCode == 503 || statusCode == 504) {
        if(requestPath != errorApiCall) {
          console.log("Error Block: " + response.data);

          const currentPage = typeof window !== 'undefined' ? window.location.pathname : 'unknown';
          const httpMethod = config?.method?.toUpperCase() || 'UNKNOWN';

          // Extract query params from the failed request (password fields redacted)
          let requestParams = 'N/A';
          try {
            if (config?.params && Object.keys(config.params).length > 0) {
              requestParams = serializeRequestData(config.params);
            }
          } catch {
            requestParams = 'Unable to extract request params';
          }

          // Extract request body/payload from the failed request (password fields redacted)
          let requestPayload = 'N/A';
          try {
            if (config?.data) {
              requestPayload = serializeRequestData(config.data);
            }
          } catch {
            requestPayload = 'Unable to extract request payload';
          }

          // Get IP address and append to error description
          getUserIpAddress().then((ipAddress) => {
            let errorDesc = (response.data as any)?.message?.replace(/'/g, '"') || (response.data as any)?.error?.replace(/'/g, '"') || 'Unknown error';
            errorDesc += `\n\nMethod: ${httpMethod}`;
            errorDesc += `\n\nRequest Params: ${requestParams}`;
            errorDesc += `\n\nRequest Payload: ${requestPayload}`;
            errorDesc += `\n\nIP Address: ${ipAddress}`;

            authenticationApi.post(errorApiCall, {
              p_request_path: requestPath,
              p_current_page: currentPage,
              p_ca_app: "Web",
              p_error_desc: errorDesc,
              p_ca_user_id: "",
              p_ca_user_email: "",
              p_device_info: navigator.userAgent
            }).then((response: any) => {
              console.log(response.data);

              if(statusCode !== 400) {
                showGlobalErrorModal(
                  "We are very sorry for the inconvenience. The app encountered an error. We have informed the administrator. Please try again later."
                );
              }
            });
          });
        }
      }
    } else if (request) {
      // Request was made but no response received (e.g., network error)
      errorMessage = 'No internet connection. Please check your network and try again.';
      details.error_message = errorMessage;
      details.message = errorMessage;

      customError.details = details;
    } else {
      // Error setting up the request
      errorMessage = `Request Error: ${error.message}`;
    }

    customError.message = errorMessage;

    // Log error for debugging (optional)
    console.log('API Error:', customError);

    // Reject with custom error
    return Promise.reject(customError);
  }
);

export default authenticationApi;
